/**
 * Cache-busting and stale-bundle detection.
 *
 * Two defense mechanisms:
 *
 * 1. **Chunk load error handler** — When a Vite lazy-loaded chunk 404s
 *    (stale bundle after an update), auto-reload the page once. Uses
 *    sessionStorage to prevent infinite reload loops.
 *
 * 2. **Build hash check** — On app startup, fetch the server's current
 *    build hash from `/api/build-hash` and compare it against the hash
 *    embedded at build time. If they differ, force a single reload.
 *
 * Both mechanisms set a "stale bundle detected" flag that the login page
 * can read to show a friendlier error message.
 */

const CHUNK_RELOAD_KEY = "qb_chunk_reload";
const VERSION_RELOAD_KEY = "qb_version_reload";
const STALE_DETECTED_KEY = "qb_stale_bundle";

/**
 * The build hash embedded at Vite compile time via the `define` option
 * in vite.config.ts. Vite replaces every occurrence of `__QBX_BUILD_HASH__`
 * with a string literal during the build. In dev mode this will be "unknown"
 * (buildInfo.json may not exist yet), which causes the version check to
 * skip itself — exactly the right behavior.
 */
declare const __QBX_BUILD_HASH__: string;
const EMBEDDED_BUILD_HASH: string = __QBX_BUILD_HASH__;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns true if a stale bundle was detected (and a reload just happened).
 * The login page uses this to show "Dashboard was recently updated" instead
 * of a generic "Network error". Reading the flag also clears it, so it only
 * fires once per reload cycle.
 */
export const consumeStaleReloadFlag = (): boolean => {
  try {
    const was = sessionStorage.getItem(STALE_DETECTED_KEY) === "1";
    if (was) sessionStorage.removeItem(STALE_DETECTED_KEY);
    return was;
  } catch {
    return false;
  }
};

/**
 * Clear the reload-loop-prevention flags. Call after the app mounts
 * successfully so that future build hash mismatches or chunk errors
 * can trigger a fresh reload cycle.
 *
 * This intentionally does NOT clear STALE_DETECTED_KEY — that flag is
 * consumed and cleared by the login page when it reads it.
 */
export const clearReloadGuards = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    sessionStorage.removeItem(VERSION_RELOAD_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

// ── Chunk Load Error Handling ────────────────────────────────────────────────

/**
 * Perform a one-time reload for chunk load failures.
 * Returns true if a reload was triggered (caller should bail out).
 */
const reloadOnceForChunk = (): boolean => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    sessionStorage.setItem(STALE_DETECTED_KEY, "1");
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

/**
 * Install global listeners for chunk load failures.
 *
 * Vite uses dynamic `import()` for lazy routes. When the server has new
 * bundles but the browser cached old HTML with old chunk filenames, those
 * imports 404. Two event types fire:
 *
 * - `error` with message containing "Failed to fetch dynamically imported module"
 *   or "Importing a module script failed"
 * - `vite:preloadError` (Vite-specific, for `modulepreload` link failures)
 */
export const installChunkErrorHandlers = (): void => {
  window.addEventListener("error", (event) => {
    const msg = event.message ?? "";
    if (
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed")
    ) {
      reloadOnceForChunk();
    }
  });

  // Vite emits this custom event when a modulepreload link fails to load.
  // The event is a CustomEvent with the original error in `event.payload`.
  window.addEventListener("vite:preloadError", () => {
    reloadOnceForChunk();
  });

  // Also catch unhandled promise rejections from dynamic imports.
  // Some browsers surface chunk failures this way instead of via `error`.
  window.addEventListener("unhandledrejection", (event) => {
    const msg = String(event.reason?.message ?? "");
    if (
      msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Importing a module script failed") ||
      msg.includes("error loading dynamically imported module")
    ) {
      reloadOnceForChunk();
    }
  });
};

// ── Build Hash Version Check ─────────────────────────────────────────────────

type BuildHashResponse = {
  ok: true;
  data: {
    hash: string;
    shortHash: string;
    version: string;
    builtAt: string;
  };
};

/**
 * Check the server's current build hash against the one compiled into this
 * bundle. If they differ, the user is running stale JS — reload once.
 *
 * This runs at app startup (in main.tsx). It is intentionally fire-and-forget:
 * a failure to reach the endpoint is not an error (the server might just be
 * restarting). The chunk error handler is the last-resort defense.
 */
export const checkBuildHash = async (): Promise<void> => {
  // Skip if we already reloaded for this session (prevent loops)
  try {
    if (sessionStorage.getItem(VERSION_RELOAD_KEY)) return;
  } catch {
    return;
  }

  // Skip in dev mode — hash is always "unknown"
  if (EMBEDDED_BUILD_HASH === "unknown") return;

  try {
    const res = await fetch("/api/build-hash", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return;

    const body = (await res.json()) as BuildHashResponse;
    if (!body.ok) return;

    const serverHash = body.data.hash;
    if (!serverHash || serverHash === "unknown") return;

    if (serverHash !== EMBEDDED_BUILD_HASH) {
      sessionStorage.setItem(VERSION_RELOAD_KEY, "1");
      sessionStorage.setItem(STALE_DETECTED_KEY, "1");
      window.location.reload();
    }
  } catch {
    // Network failure — server might be restarting. Do nothing; the chunk
    // error handler will catch any actual 404s on lazy imports.
  }
};
